import { movies } from "@/mocks/movies";
import { tvShows } from "@/mocks/tvshows";
import { newsItems } from "@/mocks/news";
import type { ContentItem, ContentType, NewsItem, SearchScope } from "@/types/content";

export const allContent: ContentItem[] = [...(movies as ContentItem[]), ...(tvShows as ContentItem[])];
export const allNews: NewsItem[] = newsItems as NewsItem[];

export const contentGenres: string[] = Array.from(
  new Set(allContent.flatMap((item) => item.genres)),
);

export const newsCategories: string[] = ["全部", "电影新闻", "电视剧新闻", "业界动态"];

export const yearRanges: { label: string; test: (year: number) => boolean }[] = [
  { label: "全部年代", test: () => true },
  { label: "2024 年至今", test: (y) => y >= 2024 },
  { label: "2020 - 2023", test: (y) => y >= 2020 && y <= 2023 },
  { label: "2019 及以前", test: (y) => y <= 2019 },
];

export function getContentById(id: string): ContentItem | undefined {
  return allContent.find((item) => item.id === id);
}

export function getContentByType(type: ContentType): ContentItem[] {
  return allContent.filter((item) => item.type === type);
}

export function getHotContent(count: number): ContentItem[] {
  return [...allContent].sort((a, b) => b.heat - a.heat).slice(0, count);
}

export function getLatestContent(count: number): ContentItem[] {
  return [...allContent].sort((a, b) => b.year - a.year).slice(0, count);
}

export function getRelatedContent(item: ContentItem, count = 4): ContentItem[] {
  return allContent
    .filter((other) => other.id !== item.id)
    .map((other) => {
      const shared = other.genres.filter((genre) => item.genres.includes(genre)).length;
      const sameType = other.type === item.type ? 1 : 0;
      return { other, score: shared * 10 + sameType * 3 + other.heat / 10000 };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, count)
    .map((entry) => entry.other);
}

export function getNewsById(id: string): NewsItem | undefined {
  return allNews.find((item) => item.id === id);
}

export function getLatestNews(count: number): NewsItem[] {
  return [...allNews].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, count);
}

export function getNewsForContent(contentId: string, count = 3): NewsItem[] {
  return allNews.filter((item) => item.relatedId === contentId).slice(0, count);
}

export function getNewsByCategory(category: string): NewsItem[] {
  if (category === "全部") return getLatestNews(allNews.length);
  return getLatestNews(allNews.length).filter((item) => item.category === category);
}

export interface SearchResults {
  content: ContentItem[];
  news: NewsItem[];
}

export function searchAll(keyword: string, scope: SearchScope = "all"): SearchResults {
  const query = keyword.trim().toLowerCase();
  const matchedContent =
    scope === "news"
      ? []
      : allContent.filter((item) => {
          if (scope !== "all" && item.type !== scope) return false;
          if (!query) return true;
          return [
            item.title,
            item.originalTitle,
            item.director,
            item.region,
            item.summary,
            ...item.genres,
            ...item.cast,
          ]
            .join(" ")
            .toLowerCase()
            .includes(query);
        });

  const matchedNews =
    scope === "movie" || scope === "tv"
      ? []
      : allNews.filter((item) => {
          if (!query) return true;
          return [item.title, item.excerpt, item.category, ...item.tags]
            .join(" ")
            .toLowerCase()
            .includes(query);
        });

  return { content: matchedContent, news: matchedNews };
}

export function formatDate(date: string): string {
  const [year, month, day] = date.split("-");
  return `${year} 年 ${Number(month)} 月 ${Number(day)} 日`;
}